<template>
	<Drawer @requestUnmount="hide" initialWidth="md">

		<template #header>
			<div class="font-semibold text-md">
				<template v-if="inUpdateMode">
					<template v-if="action === 'duplicate'">
						{{ $t('Duplicate collection') }}
					</template>
					<template v-else>
						{{ $t('Edit collection') }}
					</template>
				</template>
				<template v-else>
					{{ $t('Add collection') }}
				</template>
			</div>
		</template>

		<template #default="{ hide }">
			<form @submit.prevent="save">
				<div class="pb-16 px-6">
					<div class="bg-base pt-4">
						<p v-if="goalCollectionsStore.errors?.generic" class="text-red-600 text-xs">
							{{ goalCollectionsStore.errors?.generic.toString() }}
						</p>

						<div class="grid grid-cols-6 gap-6 row">

							<div class="col-span-6 sm:col-span-6">
								<sms-input type="text" autofocus v-model="editableModel.name" :label="$t('Name')"
										   :validationError="goalCollectionsStore.errors?.fields.name"></sms-input>
							</div>

						</div>
					</div>

					<div class="border-b border-base-200 mt-8">
						{{ $t('Classrooms in which this collection is used') }}
					</div>

					<div v-if="!classroomsStore.items.length" class="text-base-content-light py-4 italic text-center">
						{{ $t('No classrooms yet') }}
					</div>

					<div v-else class="flex flex-col mt-4">
						<div class="alert alert-info text-sm items-start mb-4">
							{{ $t('Keep in mind that you can only use one grade-level goal collection per classroom.') }}
						</div>
						<div v-for="(classroomItem, index) in availableClassrooms" class="flex justify-between w-full px-4 py-1 rounded">
							<label
								:for="'cr-' + classroomItem.classroom.id"
								class="hover:cursor-pointer"
							>
								<div class="flex gap-3 items-center">
									<input
										type="checkbox"
										:id="'cr-' + classroomItem.classroom.id"
										class="checkbox checkbox-sm checkbox-primary"
										:checked="editableModel.isClassroomSelected(classroomItem.classroom)"
										@click.prevent="toggleClassroom(classroomItem.classroom);"
										:class="classroomItem.disabled && !editableModel.isClassroomSelected(classroomItem.classroom) ? 'opacity-50' : ''"
									/>
									<span :class="classroomItem.disabled ? '' : ''">{{ classroomItem.classroom.name }}</span>

								</div>
								<p v-if="classroomItem.activeCollection" class="text-sm pl-8 text-danger">
									{{ $t('Replacing') }}: <strong>{{ classroomItem.activeCollection.name }}</strong>
								</p>
							</label>
						</div>
					</div>
				</div>

				<div class="drawer-action-bar" :class="[inUpdateMode ? 'justify-between' : 'justify-end']">
					<Dropdown v-if="inUpdateMode" position="top-start">
						<template #trigger>
							<button type="button" class="btn" :class="[saving? 'btn-disabled':'btn-danger btn-outline']">
								<span v-show="deleting" class="loading loading-spinner"></span>
								{{ $t('Archive') }}
							</button>
						</template>
						<template #content="dialogActions">
							<DropdownDialogConfirm
								:confirm-label="$t('Yes')"
								:cancel-label="$t('No')"
								confirm-style="danger"
								class="w-96"
								@cancel="dialogActions.close"
								@confirm="confirmRemove(dialogActions)"
							>
								<span class="text-danger">{{ $t('Are you sure you want to archive this item?') }}</span>
							</DropdownDialogConfirm>
						</template>
					</Dropdown>


					<div class="flex justify-end gap-1">
						<button type="button" class="btn btn-ghost" @click="hide"
								:class="[saving || deleting ? 'hidden' : '']">
							{{ $t('Cancel') }}
						</button>
						<button type="submit" class="btn" :class="[deleting ? 'btn-disabled' : 'btn-primary']">
							<span v-show="saving" class="loading loading-spinner"></span>

							<template v-if="action === 'duplicate'">
								{{ $t('Duplicate') }}
							</template>

							<template v-else>
								{{ $t('Save') }}
							</template>
						</button>
					</div>
				</div>
			</form>
		</template>
	</Drawer>

	<Modal v-if="overrideCurriculumWarningClassroom" @requestUnmount="closeOverrideCurriculumWarning">
		<template #header>&nbsp;</template>

		<template #default="modalActions">
			<p class="py-4">
			{{ $t('This classroom is currently using a different grade-level goal collection. Sure you want to switch?') }}
			</p>

			<div class="modal-footer flex justify-end gap-1">
				<button class="btn btn-ghost" @click="modalActions.hide">{{ $t('No') }}</button>
				<button class="btn btn-danger" @click="toggleClassroom(overrideCurriculumWarningClassroom, true)">{{ $t('Yes') }}</button>
			</div>
		</template>
	</Modal>

</template>

<script lang="ts">
import { mapStores } from "pinia";

import { useClassroomsStore } from "@/stores/Classrooms.store";
import { useUsersStore } from "@/stores/Users.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";

import Drawer from "@/components/ui/Drawer.v2.vue";
import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownDialogConfirm from "@/components/ui/DropdownDialogConfirm.vue";
import { ArchiveBoxArrowDownIcon } from "@heroicons/vue/24/outline";
import {GoalCollection} from "@/models/GoalCollection.model.ts";
import {useGoalCollectionsStore} from "@/stores/GoalCollections.store.ts";
import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";
import {Classroom} from "@/models/Classroom.model.ts";
import {Goal} from "@/models/Goal.model.ts";
import Modal from "@/components/ui/Modal.vue";


export default {

	emits: ['requestClose'],

	props: {
		goalCollection: {
			type: GoalCollection,
			required: true,
		},

		action: {
			type: String,
			default: 'edit', // 'create', 'edit' or 'duplicate'
		},
	},
	components: {
		Modal,
		PupilSymbol,
		Drawer,
		Dropdown,
		DropdownDialogConfirm,
		ArchiveBoxArrowDownIcon,
	},
	data() {
		return {
			saving: false,
			deleting: false,
			originalModel: null as GoalCollection | null,
			editableModel: null as GoalCollection | null,
			overrideCurriculumWarningClassroom: null as Classroom | null,
		}
	},
	computed: {
		...mapStores(
			useGoalCollectionsStore,
			useUsersStore,
			useClassroomsStore
		),

		inUpdateMode() {
			return this.goalCollection && this.goalCollection.id;
		},

		/**
		 * Returns a list of classrooms with a disabled state.
		 * Disabled means that the classroom is already selected in another goal collection.
		 */
		availableClassrooms(): {
			classroom: Classroom,
			disabled: boolean,
			activeCollection?: GoalCollection | null,
		}[] {
			// Filter classrooms that do not have a curriculum set
			return this.classroomsStore.items.map(
				(classroom) => {
					const activeCurriculum = this.goalCollectionsStore.items.find(
						(goalCollection: GoalCollection) => {
							return goalCollection.isClassroomSelected(classroom) && goalCollection.id !== this.editableModel?.id;
						}
					);

					return {
						classroom,
						disabled: activeCurriculum !== undefined,
						activeCollection: activeCurriculum || null,
					};
				}
			);
		}
	},

	methods: {

		hide() {
			this.goalCollectionsStore.clearErrors();
			this.$emit('requestClose');
		},

		async confirmRemove(dialogActions) {

			dialogActions.close();

			if (this.deleting) {
				return;
			}
			this.deleting = true;

			try {
				await this.goalCollectionsStore.delete(this.originalModel);
				this.deleting = false;

				this.hide();

				this.$notify({
					text: this.$t('The goal collection has been removed'),
					type: "success",
				});

			} catch (e: any) {
				this.deleting = false;

				this.$notify({
					text: e.toString(),
					type: "error",
				});

				if (!(e instanceof ApiErrorMessageBag)) {
					throw e;
				}
			}
		},

		async save(event) {

			if (this.saving) {
				return;
			}
			this.saving = true;

			try {

				// First make sure there are no classrooms selected that are already using a different goal collection.
				// If so, we need to first remove the classroom from that collection.
				await this.removeClassroomsFromUnselectedCollections();

				if (this.action === 'duplicate') {

					await this.goalCollectionsStore.duplicate(this.editableModel);

				} else {

					await this.goalCollectionsStore.save(this.editableModel);

				}

				this.saving = false;
				this.hide();

				this.$notify({
					text: this.$t('The goal collection has been saved'),
					type: "success",
				});

			} catch (e) {

				this.$notify({
					text: e.toString(),
					type: "error",
				});

				// error message is populated now.
				this.saving = false;

				if (!(e instanceof ApiErrorMessageBag)) {
					throw e;
				}
			}
		},

		/**
		 * For all selected classrooms, check if they were selected in another goal collection
		 * and remove them from those collections.
		 */
		async removeClassroomsFromUnselectedCollections() {

			const collectionsToRemoveClassroomsFrom: {
				goalCollection: GoalCollection,
				classrooms: Classroom[],
			}[] = [];

			for (const classroomItem of this.availableClassrooms) {

				const classroom = classroomItem.classroom;
				if (!this.editableModel.isClassroomSelected(classroom)) {
					// Classroom is not selected in this collection, so we don't need to do anything
					continue;
				}

				const activeCollection = this.goalCollectionsStore.items.find(
					(goalCollection: GoalCollection) => {
						return goalCollection.isClassroomSelected(classroom) && goalCollection.id !== this.editableModel?.id;
					}
				);

				if (activeCollection) {
					let collectionToRemove = collectionsToRemoveClassroomsFrom.find(
						(item) => item.goalCollection.id === activeCollection.id
					);

					if (!collectionToRemove) {
						collectionToRemove = {
							goalCollection: activeCollection,
							classrooms: [],
						};
						collectionsToRemoveClassroomsFrom.push(collectionToRemove);
					}

					collectionToRemove.classrooms.push(classroom);
				}
			}

			if (collectionsToRemoveClassroomsFrom.length === 0) {
				// No classrooms to remove from other collections
				return;
			}

			// Now remove the classrooms from the collections
			for (const collectionToRemove of collectionsToRemoveClassroomsFrom) {
				for (const classroom of collectionToRemove.classrooms) {
					collectionToRemove.goalCollection.unselectClassroom(classroom)
				}

				// Save the collection after removing the classrooms
				try {
					await this.goalCollectionsStore.save(collectionToRemove.goalCollection);
				} catch (e) {
					this.$notify({
						text: 'Failed to remove classrooms from goal collection: ' + e.toString(),
						type: "error",
					});
					throw e;
				}
			}

		},

		toggleClassroom(classroom: Classroom, override = false) {

			this.overrideCurriculumWarningClassroom = null;

			// Is this classroom currently using a different goal collection?
			// Then show a warning and ask if the user wants to switch.
			if (!override && !this.editableModel.isClassroomSelected(classroom)) {
				const activeCollection = this.goalCollectionsStore.items.find(
					(goalCollection: GoalCollection) => {
						return goalCollection.isClassroomSelected(classroom) && goalCollection.id !== this.editableModel?.id;
					}
				);

				if (activeCollection) {
					this.overrideCurriculumWarningClassroom = classroom;
					return;
				}
			}

			// Timeout required since the click preventDefault is apparently causing the checkbox to not read the new value
			setTimeout(() => {
				this.editableModel.toggleClassroom(classroom)
			}, 0);

		},


		closeOverrideCurriculumWarning() {
			this.overrideCurriculumWarningClassroom = null;
		}
	},

	beforeMount() {
		if (this.goalCollection && this.goalCollection.id) {
			this.originalModel = this.goalCollection;
			// clone the store model to allow for manipulation without instantly affecting the store
			this.editableModel = this.goalCollection.clone();

		} else {
			this.editableModel = this.goalCollection.clone();
		}

		this.classroomsStore.loadOnce();
	},
}
</script>
